import { useState, useEffect, useCallback, useRef } from 'react';
import { apiRequest } from '../services/api';
import { useAuth } from '../context/AuthContext';

const OKUNDU_KEY = 'hf_okundu_bildirimler';
const KONTROL_MS = 15 * 1000;

function getOkunduSet() {
  try { return new Set(JSON.parse(localStorage.getItem(OKUNDU_KEY) || '[]')); }
  catch { return new Set(); }
}
function saveOkundu(set) {
  try { localStorage.setItem(OKUNDU_KEY, JSON.stringify([...set])); } catch {}
}

function parseISO(iso) {
  if (!iso) return null;
  if (iso.endsWith('Z') || iso.match(/[+-]\d{2}:\d{2}$/)) return new Date(iso);
  const [datePart, timePart = '00:00:00'] = iso.split('T');
  const [y, m, g] = datePart.split('-').map(Number);
  const [h, mi, s] = timePart.split(':').map(Number);
  return new Date(y, m - 1, g, h, mi, s || 0);
}

function zamanFarki(d) {
  if (!d) return '';
  const fark = Math.floor((Date.now() - d.getTime()) / 1000);
  if (fark < 60)    return 'Az önce';
  if (fark < 3600)  return `${Math.floor(fark / 60)} dk önce`;
  if (fark < 86400) return `${Math.floor(fark / 3600)} sa önce`;
  return `${Math.floor(fark / 86400)} gün önce`;
}

export function useBildirimler() {
  const [bildirimler, setBildirimler] = useState([]);
  const [okunduSet,   setOkunduSet]   = useState(getOkunduSet);
  // Alarm ID'leri için ayrı "force unread" seti — session içi, kalıcı değil
  const [alarmUnread, setAlarmUnread] = useState(new Set());
  const alarmUnreadRef = useRef(new Set());
  const { hasYetkiOrAdmin } = useAuth();
  const permRef = useRef(hasYetkiOrAdmin);
  permRef.current = hasYetkiOrAdmin;

  const fetchBildirimler = useCallback(async () => {
    try {
      const bugunBas = new Date(); bugunBas.setHours(0, 0, 0, 0);
      const simdi    = new Date();

      const [rezervasyonlar, bakiyeler, mesajlar] = await Promise.all([
        apiRequest('rezervasyonlar').catch(() => []),
        apiRequest('rezervasyonlar/bakiyeler').catch(() => []),
        permRef.current('ayarlar')
          ? apiRequest('misafir-mesaj?okunmamis=true').catch(() => [])
          : Promise.resolve([]),
      ]);

      const yeniler      = [];
      const yeniAlarmIds = []; // bu fetch'te ilk kez görülen tetiklenen alarmlar

      // ── 1. Bugün oluşturulan rezervasyonlar ──
      (rezervasyonlar || []).forEach(r => {
        const olusturma = parseISO(r.olusturmaTarihi);
        if (!olusturma || olusturma < bugunBas) return;
        yeniler.push({
          id:           `rez_${r.rezervasyonId}`,
          tip:          'rezervasyon',
          ikon:         '📥',
          baslik:       'Yeni rezervasyon',
          icerik:       `${r.rezervasyonMisafirler?.[0]?.misafir?.adSoyad || 'Misafir'} · Oda ${r.odaNo || '—'} · ${r.acenteAd || 'Direkt'}`,
          zaman:        zamanFarki(olusturma),
          tarih:        olusturma,
          path:         '/rezervasyon',
          rezervasyonId: r.rezervasyonId,
        });
      });

      // ── 2. Alarm zamanı gelmiş bakiyeler ──
      (bakiyeler || []).forEach(b => {
        if (!b.alarmTarihi) return;
        const alarmD = parseISO(b.alarmTarihi);
        if (!alarmD || alarmD > simdi) return;

        const id = `alarm_${b.rezervasyonId}`;

        // alarmUnreadRef'te yoksa → bu session'da ilk kez tetiklendi
        if (!alarmUnreadRef.current.has(id)) {
          yeniAlarmIds.push(id);
          alarmUnreadRef.current.add(id); // ref'i hemen güncelle
        }

        yeniler.push({
          id,
          tip:          'alarm',
          ikon:         '🔔',
          baslik:       `Alarm: ${b.adSoyad || 'Misafir'}`,
          icerik:       `Oda ${b.odaNo} · Kalan: ${(b.kalanBakiye||0).toLocaleString('tr-TR',{minimumFractionDigits:2})} TL${b.alarmAciklamasi ? ' · ' + b.alarmAciklamasi : ''}`,
          zaman:        'Bugün',
          tarih:        alarmD,
          path:         '/kasa/bakiyeler',
          rezervasyonId: b.rezervasyonId,
        });
      });

      // Yeni alarm varsa: state'e ekle + okundu'dan sil
      if (yeniAlarmIds.length > 0) {
        setAlarmUnread(prev => {
          const yeni = new Set(prev);
          yeniAlarmIds.forEach(id => yeni.add(id));
          return yeni;
        });
        setOkunduSet(prev => {
          const yeni = new Set(prev);
          let degisti = false;
          yeniAlarmIds.forEach(id => {
            if (yeni.has(id)) { yeni.delete(id); degisti = true; }
          });
          if (degisti) saveOkundu(yeni);
          return degisti ? yeni : prev;
        });
      }

      // ── 3. Ödeme bekleyen ──
      // Bildirim yalnızca ŞU AN OTELDE OLAN (C-IN yapılmış) rezervasyonları
      // sayar; Bakiyeler sayfasının toplamı ise giriş bekleyen ve çıkışı
      // yapılmış kayıtları da içerir. İki rakam bu yüzden farklı olabiliyor —
      // başlıkta kapsam açıkça yazılıyor ki tutarsızlık gibi görünmesin.
      const odemebekleyen = (bakiyeler || []).filter(b =>
        b.kalanBakiye > 0 && b.odemeDurumu === 'Ödeme bekliyor' && b.durum === 'C-IN Yapıldı'
      );
      if (odemebekleyen.length > 0) {
        const toplam = odemebekleyen.reduce((s, b) => s + (b.kalanBakiye || 0), 0);
        yeniler.push({
          id:    'odeme_bekleyen',
          tip:   'odeme',
          ikon:  '💳',
          baslik: 'Konaklayanlarda ödeme bekleyen bakiye',
          icerik: `Otelde bulunan ${odemebekleyen.length} rezervasyonda toplam ` +
                  `${toplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL. ` +
                  `Giriş bekleyen ve çıkışı yapılmış kayıtlar için Bakiyeler sayfasına bakın.`,
          zaman:  'Şu an',
          tarih:  new Date(),
          path:   '/kasa/bakiyeler',
        });
      }

      // ── 4. Misafir mesajları (okunmamış) ──
      (mesajlar || []).forEach(m => {
        const olusturma = parseISO(m.olusturmaTarihi);
        yeniler.push({
          id:    `mesaj_${m.misafirMesajId}`,
          tip:   'mesaj',
          ikon:  '✉️',
          baslik: 'Yeni mesaj',
          icerik: `${m.adSoyad}${m.telefon ? ' · ' + m.telefon : ''} — ${m.mesaj?.substring(0, 60)}${m.mesaj?.length > 60 ? '...' : ''}`,
          zaman:  zamanFarki(olusturma),
          tarih:  olusturma,
          path:   '/website/mesajlar',
          mesajId: m.misafirMesajId,
        });
      });

      yeniler.sort((a, b) => (b.tarih?.getTime()||0) - (a.tarih?.getTime()||0));
      setBildirimler(yeniler);
    } catch (err) {
      console.error('Bildirimler yüklenemedi:', err);
    }
  }, []);

  useEffect(() => {
    fetchBildirimler();
    const t = setInterval(fetchBildirimler, KONTROL_MS);
    return () => clearInterval(t);
  }, [fetchBildirimler]);

  const okunduYap = useCallback((id) => {
    setOkunduSet(prev => {
      const yeni = new Set(prev); yeni.add(id); saveOkundu(yeni); return yeni;
    });
    setAlarmUnread(prev => {
      const yeni = new Set(prev); yeni.delete(id); return yeni;
    });
    alarmUnreadRef.current.delete(id);
    if (id.startsWith('mesaj_')) {
      const mesajId = parseInt(id.replace('mesaj_', ''), 10);
      apiRequest(`misafir-mesaj/${mesajId}/okundu`, 'PUT').catch(() => {});
    }
  }, []);

  const tumunuOkunduYap = useCallback(() => {
    setOkunduSet(prev => {
      const yeni = new Set(prev);
      bildirimler.forEach(b => yeni.add(b.id));
      saveOkundu(yeni);
      return yeni;
    });
    setAlarmUnread(new Set());
    alarmUnreadRef.current.clear();
    bildirimler.forEach(b => {
      if (b.tip === 'mesaj') {
        apiRequest(`misafir-mesaj/${b.mesajId}/okundu`, 'PUT').catch(() => {});
      }
    });
  }, [bildirimler]);

  const bildirimlerWithOkundu = bildirimler.map(b => ({
    ...b,
    // Alarm için: alarmUnread'de varsa KESİNLİKLE okunmamış
    // Mesaj için: okunduYap çağrılana kadar okunmamış (API'den sadece okunmamışlar geliyor)
    okundu: b.tip === 'alarm'
      ? (!alarmUnread.has(b.id) && okunduSet.has(b.id))
      : b.tip === 'mesaj'
        ? okunduSet.has(b.id)
        : okunduSet.has(b.id),
  }));

  const okunmamisSayi = bildirimlerWithOkundu.filter(b => !b.okundu).length;

  return { bildirimler: bildirimlerWithOkundu, okunmamisSayi, okunduYap, tumunuOkunduYap, yenile: fetchBildirimler };
}